import groupBy from "lodash/groupBy";
import map from "lodash/map";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { useImportanceLabelMap } from "PFApp/hooks/use_importance_label_map";
import { CustomValuePill } from "PFComponents/custom_value_pill";
import { Tile, TileGroup } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { requiresApproval, useCustomValueToString } from "PFCore/helpers/custom_value";
import { ActivityPureCustomValue, CustomType, Importance } from "PFTypes";

import css from "./match_attributes.module.scss";

type AttributeTileProps = {
  isRankable: boolean;
  customType: CustomType;
  matchKeywords: ActivityPureCustomValue[];
  onAttributeClick: (attr: ActivityPureCustomValue) => void;
};

const ORDERED_IMPORTANCES = [Importance.Essential, Importance.Supporting, Importance.Required];

export const AttributeTile = ({
  isRankable,
  matchKeywords,
  customType,
  onAttributeClick
}: AttributeTileProps) => {
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const groupedKeywords = groupBy(matchKeywords, "importance");
  const importanceLabelMap = useImportanceLabelMap();
  const { getCustomValueString } = useCustomValueToString();

  return (
    <TileGroup>
      {map(ORDERED_IMPORTANCES, (importance) => {
        const keywords = groupedKeywords[importance] || [];

        if (keywords.length === 0) {
          return null;
        }

        return (
          <Tile className={css.attributeTile} border paddingVariant="md">
            <Typography withMargin variant="labelRegular">
              {importanceLabelMap[importance]}
            </Typography>
            {map(keywords, (keyword) => (
              <CustomValuePill
                name={getCustomValueString(keyword)}
                experienceLevels={experienceLevels}
                experienceLabelMap={experienceLabelMap}
                customTypeAriaLabel={customType.display_as}
                isRequirement={isRankable}
                isPendingApproval={requiresApproval(customType, keyword)}
                experience={isRankable ? keyword.required_experience : undefined}
                onClick={() => onAttributeClick(keyword)}
              />
            ))}
          </Tile>
        );
      })}
    </TileGroup>
  );
};
